import { useRecordContext, useDataProvider } from "react-admin";
import { FetchData } from "./dataFetcher";
import { useEffect, useState, useRef } from "react";
import * as d3 from "d3";
import { GetYMax, RenderTooltipText } from "./fso2Scatter";
import { bwColours } from "../lib/theme";

const containerHeight = 400;
const containerWidth = 300;
const tooltipHeight = 100;
const margin = { top: 20, left: 50, bottom: 10, right: 100 };

const styles = {
  container: {
    width: `${containerWidth}px`,
    // Stay on screen on narrow phones.
    maxWidth: "100%",
    height: `${containerHeight}px`,
  },
  svg: {
    width: `${containerWidth}px`,
    height: `${containerHeight - 100}px`,
  },
  tooltip: {
    width: `${containerWidth}px`,
    height: `${tooltipHeight}px`,
  },
};

function CreateTranslationStr(x, y) {
  return `translate(${x},${y})`;
}

function setTooltip(ref) {
  let tooltip = d3.select(ref).style("opacity", 1);
  return tooltip;
}
function setScales(categories, yMax, styles) {
  let xScale = d3
    .scaleBand()
    .domain(categories)
    .range([margin.left, containerWidth - margin.left])
    .padding(0.05);

  let yScale = d3
    .scaleLinear()
    .domain([0, yMax + 5])
    .range([
      containerHeight - tooltipHeight - margin.top,
      margin.bottom + margin.top,
    ]);

  return { xScale: xScale, yScale: yScale };
}
function setAxesToCanvas(canvas, scales) {
  let xAxis = d3.axisBottom().scale(scales.xScale);

  canvas
    .append("g")
    .attr("class", "x-axis")
    .attr(
      "transform",
      CreateTranslationStr(0, containerWidth - margin.top - margin.bottom),
    )
    .call(xAxis);

  // Add scale to Y axis
  let yAxis = d3.axisLeft().scale(scales.yScale);
  canvas
    .append("g")
    .attr("class", "y-axis")
    .attr("transform", CreateTranslationStr(margin.left, -margin.bottom))
    .call(yAxis);
}
function setPointsToCanvas(canvas, data, scales, tooltip) {
  var mouseover = function () {
    tooltip.style("opacity", 1);
  };
  var mousemove = function (d, i) {
    const text = RenderTooltipText(i);

    tooltip
      .html(text)
      .style("left", d3.pointer(this)[0] + 70 + "px")
      .style("top", d3.pointer(this)[1] + "px");
  };
  var mouseleave = function () {
    tooltip.style("opacity", 0);
  };
  // Touch devices have no hover: tapping a point shows its details.
  var touchshow = function (event, _d, i) {
    event.stopPropagation();
    tooltip.html(RenderTooltipText(i)).style("opacity", 1);
  };
  const spreadScale = d3
    .scaleLinear()
    .domain([0, data.length - 1])
    .range([scales.xScale.bandwidth() * 0.2, scales.xScale.bandwidth() * 0.8]);

  canvas
    .selectAll("circle")
    .data(data)
    .enter()
    .append("circle")
    .attr("cx", (d, i) => scales.xScale(d.lot) + spreadScale(i))
    .attr("cy", function (d) {
      return scales.yScale(parseFloat(d.fso2) + 1.2);
    })
    .attr("r", 5)
    .style("fill", bwColours.primary.red)
    .style("opacity", 0.4)
    .attr("clip-path", "url(#clip)")
    .on("mouseover", mouseover)
    .on("mousemove", mousemove)
    .on("mouseleave", mouseleave)
    .on("touchstart", touchshow)
    .on("touchmove", touchshow);
}

const VCScatterPlot = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const svgRef = useRef(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (!record) return;

    const apiFilter = {
      ids: record?.eventFSO2ReadIDs,
    };

    FetchData(
      dataProvider,
      setData,
      setLoading,
      (err) => {
        if (err) console.log(err);
      },
      apiFilter,
    );
  }, [record, dataProvider]);

  useEffect(() => {
    if (!record || !data) return;

    let canvas = d3.select(svgRef.current);

    // Tapping the empty chart area clears a shown touch tooltip.
    canvas.on("touchstart", () =>
      d3.select(tooltipRef.current).style("opacity", 0),
    );

    // Define clipping path
    canvas
      .append("defs")
      .append("clipPath")
      .attr("id", "clip")
      .append("rect")
      .attr("x", margin.left)
      .attr("width", styles.svg.width)
      .attr("height", styles.svg.height);

    var tooltip = setTooltip(tooltipRef.current);
    const yMax = GetYMax(data);
    const scales = setScales([record.lotName], yMax, styles);
    setAxesToCanvas(canvas, scales);
    setPointsToCanvas(canvas, data, scales, tooltip);
  }, [data, record, loading]);

  return (
    <div id="container" style={styles.container}>
      {loading ? null : (
        <>
          <svg ref={svgRef} style={styles.svg} />
          <div ref={tooltipRef} style={styles.tooltip} />
        </>
      )}
    </div>
  );
};

export default VCScatterPlot;
